import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSupportAuth } from '@/context/SupportAuthContext';
import { BRAND_NAME, LOGO } from '@/lib/brand';

export function SupportLoginPage() {
  const { signIn, user, agent, admin, loading, configured } = useSupportAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from || '/support/dashboard';

  if (!loading && user && !user.isAnonymous && (agent?.active || admin)) {
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await signIn(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-b from-slate-50 to-background dark:from-white/[0.03] dark:to-background">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center space-y-2">
          <img src={LOGO.withoutBg} alt={BRAND_NAME} className="h-12 w-12 mx-auto object-contain dark:hidden" />
          <img src={LOGO.dark} alt="" className="h-12 w-12 mx-auto object-contain rounded-full hidden dark:block" />
          <h1 className="text-xl font-semibold">Support Console</h1>
          <p className="text-sm text-muted-foreground">Sign in with your agent account</p>
        </div>

        {!configured && (
          <p className="text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 rounded-lg p-3">
            Firebase env vars are missing. See docs/HUMAN_SUPPORT_SETUP.md.
          </p>
        )}

        <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full" disabled={submitting || !configured}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>

        <p className="text-center text-xs text-muted-foreground">
          <Link to="/" className="underline-offset-2 hover:underline">Back to site</Link>
        </p>
      </div>
    </div>
  );
}
